import { HTTP_STATUS_FORBIDDEN, startLogin } from "@auth/login"
import type { QueryClient } from "@tanstack/react-query"
import {
  Outlet,
  createRootRouteWithContext,
  redirect,
} from "@tanstack/react-router"
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools"
import { ResponseError } from "@utils/request"
import type { FC } from "react"

import { ErrorScreen } from "./-components/ErrorScreen"

export type RouterContext = {
  queryClient: QueryClient
}

const RootLayout: FC = () => (
  <>
    <Outlet />
    <TanStackRouterDevtools position="bottom-right" />
  </>
)

/** Maps a thrown error to the title/message for the shared error screen. */
const errorScreenProps = (error: Error): { title: string; message: string } => {
  if (
    error instanceof ResponseError &&
    error.statusCode === HTTP_STATUS_FORBIDDEN
  ) {
    if (error.detail.includes("solutionUid")) {
      return {
        title: "No solutions available",
        message:
          "Your account isn't attached to any solutions. Contact your administrator to get access, then press the refresh button below.",
      }
    }
    return {
      title: "Access denied",
      message:
        "An admin role is required to access the Co-pilot Backoffice app. Talk to your administrator to get access, and press the refresh button below.",
    }
  }
  return { title: "Something went wrong", message: error.message }
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: ({ location }) => {
    if (location.pathname === "/") {
      throw redirect({
        to: "/base-modules",
      })
    }
  },
  component: RootLayout,
  // Reload re-fetches `/auth/user` — the right recovery once access is granted,
  // and it works even for users who can't call logout.
  errorComponent: ({ error }) => (
    <ErrorScreen
      {...errorScreenProps(error)}
      action={{ label: "Refresh", onClick: () => void startLogin() }}
    />
  ),
})
